import { useEffect, useState } from 'react'
import { PanelLoading } from './ui.jsx'
import { hubspotCall } from '../lib/hubspot.js'
import { StatusPill } from './DataTable.jsx'
import { BuildingIcon, UserIcon, PhoneIcon, ClockIcon, TextIcon, CopyIcon, CheckIcon } from './icons.jsx'
import {
  normalizeCallRecord,
  callOutcome,
  callPickup,
  callSourceLabel,
  callContentLabels,
  formatDurationMs,
  formatPhone,
  isHubSpotPhoneCall,
  formatClock,
  speakerName,
  transcriptPlainText,
} from '../lib/callContent.js'
import { longDay, chicagoClock } from '../lib/activityMetrics.js'

// ONE CALL, READ IN FULL. Opened from a row of the Activity page's calls
// ledger (or a call block on the Day timeline). Everything here is what the
// HubSpot mirror holds for the call: the outcome and whether anyone picked
// up, the numbers, the FULL TRANSCRIPT — which the mirror makes itself from
// HubSpot's recording, one party per recording channel, because HubSpot
// exposes no API for its own — HubSpot's AI summary, and the rep's notes.
//
// Nothing here is innerHTML. The summary and the transcript arrive as text
// (the lambda parses HubSpot's markup) and are drawn with elements of our own.

const cache = new Map() // id -> { at, result }
const CACHE_MS = 60000
// A transcript still on its way is asked for again after this long, so the
// page fills in on its own when the mirror's next run lands it.
const PENDING_RECHECK_MS = 60000

const FACT = 'rounded-[10px] border border-line bg-panel px-3 py-2'
const FACT_LABEL = 'mb-[2px] flex items-center gap-x-1.5 text-[11.5px] font-medium uppercase tracking-[0.04em] text-faint'
const LINK = 'font-semibold text-primary-dark hover:underline'
const CARD = 'mb-5 rounded-[12px] border border-line bg-surface p-4'
const SPEAKER_TONE = {
  rep: 'text-primary-dark',
  contact: 'text-[#6d28d9] dark:text-[#c4b5fd]',
  unknown: 'text-dim',
}

function Fact({ icon: Icon, label, children }) {
  return (
    <div className={FACT}>
      <div className={FACT_LABEL}>
        {Icon && <Icon className="h-[13px] w-[13px]" aria-hidden="true" />}
        {label}
      </div>
      <div className="text-[13.5px] text-ink">{children}</div>
    </div>
  )
}

function SummarySections({ sections }) {
  return (
    <div className="flex flex-col gap-y-3">
      {sections.map((s, i) => (
        <div key={`${s.heading}-${i}`}>
          {s.heading && <h4 className="m-0 mb-1 text-[13px] font-semibold text-ink">{s.heading}</h4>}
          {s.blocks.map((b, j) => {
            if (b.kind === 'sub') return <div key={j} className="mt-2 text-[13px] font-semibold text-dim">{b.text}</div>
            if (b.kind === 'li') {
              return (
                <div key={j} className="flex gap-x-2 text-[13.5px] leading-[1.5] text-ink">
                  <span className="flex-none text-faint" aria-hidden="true">•</span>
                  <span>{b.text}</span>
                </div>
              )
            }
            return <p key={j} className="m-0 text-[13.5px] leading-[1.55] text-ink">{b.text}</p>
          })}
        </div>
      ))}
    </div>
  )
}

function TranscriptSection({ call }) {
  const t = call.transcript
  const [copied, setCopied] = useState(false)
  // A mirror one deploy behind sends no transcript block: say nothing rather
  // than claim there is no transcript.
  if (!t) return null
  const lines = t.segments
  const ready = t.status === 'ready'
  async function copy() {
    try {
      await navigator.clipboard.writeText(transcriptPlainText(t))
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      /* the browser refused the clipboard; nothing to undo */
    }
  }
  return (
    <section className={CARD}>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="m-0 text-[14px] font-semibold text-ink">Transcript</h3>
        {ready && lines.length > 0 && (
          <span className="text-[12px] text-faint">
            {t.channels === 2 ? `${speakerName(t, 'rep')} and ${speakerName(t, 'contact')} · ` : ''}
            {lines.length.toLocaleString()} {lines.length === 1 ? 'line' : 'lines'}
            {t.durationS != null ? ` · ${formatClock(t.durationS)} recorded` : ''}
          </span>
        )}
        {ready && lines.length > 0 && (
          <button
            type="button"
            onClick={copy}
            className="ml-auto inline-flex cursor-pointer items-center gap-x-1.5 rounded-[8px] border border-line bg-surface px-2.5 py-1 text-[12.5px] font-semibold text-ink hover:bg-panel"
          >
            {copied ? <CheckIcon className="h-[14px] w-[14px] text-good" /> : <CopyIcon className="h-[14px] w-[14px] text-dim" />}
            {copied ? 'Copied' : 'Copy transcript'}
          </button>
        )}
      </div>
      {ready && lines.length > 0 ? (
        <>
          <div className="flex flex-col">
            {lines.map((seg, i) => (
              <div key={`${seg.start}-${i}`} className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-3 border-0 border-t border-solid border-line py-[7px] first:border-t-0">
                <span className="pt-[1px] text-[12px] tabular-nums text-faint">{formatClock(seg.start)}</span>
                <div className="min-w-0 text-[13.5px] leading-[1.55] text-ink">
                  <span className={`mr-2 font-semibold ${SPEAKER_TONE[seg.speaker] || SPEAKER_TONE.unknown}`}>{speakerName(t, seg.speaker)}</span>
                  {seg.text}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[12px] text-faint">
            Transcribed by the mirror from HubSpot’s recording{t.model ? ` with ${t.model}` : ''}.{' '}
            {t.channels === 2
              ? 'Each side of the call is recorded on its own channel, so every line names who said it.'
              : 'This recording could not be split by speaker.'}
            {t.truncated ? ' The transcript was cut to fit storage, so the end of the call is missing here.' : ''}
          </div>
        </>
      ) : t.status === 'failed' ? (
        <div className="rounded-[10px] border border-[#fde68a] bg-[#fefce8] px-3 py-2 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
          {t.reason}
        </div>
      ) : (
        <div className="text-[13px] text-dim">
          {t.status === 'pending' && <span className="mr-1 font-semibold text-ink">Transcribing…</span>}
          {t.reason || (ready ? 'The recording was transcribed and no speech was found in it.' : '')}
        </div>
      )}
    </section>
  )
}

export default function CallView({ id, memberName, onBack }) {
  const [state, setState] = useState({ status: 'loading', result: null })
  const [recheck, setRecheck] = useState(0)

  useEffect(() => {
    let cancelled = false
    const hit = cache.get(id)
    if (hit && Date.now() - hit.at < CACHE_MS && !recheck) {
      setState({ status: 'ok', result: hit.result })
      return () => {
        cancelled = true
      }
    }
    if (!recheck) setState({ status: 'loading', result: null })
    const ctrl = new AbortController()
    hubspotCall(id, { signal: ctrl.signal }).then((res) => {
      if (cancelled) return
      // Only a real answer is remembered: a transport failure must be asked
      // again on the next open, not served from memory for a minute.
      if (res && res.available) cache.set(id, { at: Date.now(), result: res })
      setState({ status: 'ok', result: res })
    })
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [id, recheck])

  const result = state.result
  const call = result && result.found ? normalizeCallRecord(result.call) : null
  const transcriptPending = Boolean(call && call.transcript && call.transcript.status === 'pending')

  useEffect(() => {
    if (!transcriptPending) return undefined
    const timer = setTimeout(() => {
      cache.delete(id)
      setRecheck((n) => n + 1)
    }, PENDING_RECHECK_MS)
    return () => clearTimeout(timer)
  }, [transcriptPending, id, recheck])

  const back = (
    <button
      type="button"
      className="mb-3 inline-flex w-fit cursor-pointer items-center gap-x-[7px] border-none bg-transparent p-0 text-[13px] font-semibold text-dim transition-colors hover:text-ink"
      onClick={onBack}
    >
      <span aria-hidden="true" className="text-[15px] leading-none">←</span>
      Back to calls
    </button>
  )

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {back}
        <PanelLoading label="Reading the call from the HubSpot mirror…" />
      </div>
    )
  }
  if (!result || !result.available) {
    const reason = (result && result.reason) || 'The HubSpot mirror could not be reached from this browser.'
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {back}
        <div className="rounded-[12px] border border-[#fde68a] bg-[#fefce8] p-4 text-[13px] text-[#a16207] dark:border-[rgba(161,98,7,0.4)] dark:bg-[rgba(161,98,7,0.14)] dark:text-[#fbbf24]">
          {reason}
          {result && result.status ? ` (HTTP ${result.status})` : ''} The call could not be read; nothing below is shown as missing.
        </div>
      </div>
    )
  }
  if (!call) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {back}
        <div className="rounded-[12px] border border-line bg-surface p-10 text-center text-dim">
          {result.reason || (result.deleted ? 'That call was deleted or merged in HubSpot.' : 'That call is not in the mirror.')}
        </div>
      </div>
    )
  }

  const outcome = callOutcome(call)
  const pickup = callPickup(call)
  const duration = formatDurationMs(call.durationMs)
  const chips = callContentLabels(call.content)
  const phone = isHubSpotPhoneCall(call)
  const direction = call.direction ? (call.direction.toUpperCase() === 'INBOUND' ? 'Inbound' : 'Outbound') : ''
  const subtitle = [call.day ? longDay(call.day) : '', call.at ? chicagoClock(call.at) : '', memberName || '', callSourceLabel(call), direction]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
      {back}
      <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="m-0 min-w-0 flex-shrink truncate text-[24px] font-bold tracking-[-0.02em] text-ink">{call.title || 'Call'}</h1>
        {outcome && (
          <StatusPill tone={outcome.opaque ? 'neutral' : 'info'}>
            <span title={outcome.opaque ? `HubSpot disposition id ${outcome.raw} — not in the portal's outcome list` : 'The outcome the rep picked in HubSpot'}>{outcome.label}</span>
          </StatusPill>
        )}
        {pickup && <StatusPill tone={pickup.tone}>{pickup.label}</StatusPill>}
        {!outcome && !pickup && (
          <span className="text-[12.5px] text-faint" title="HubSpot holds no outcome, dial status or answer disposition for this call — not recorded, which is not the same as unanswered.">
            outcome not recorded
          </span>
        )}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-dim">
        <span>{subtitle}</span>
        {chips.length > 0 && (
          <span className="flex flex-wrap gap-1">
            {chips.map((c) => (
              <span key={c.key} className="rounded-full border border-line bg-panel px-[7px] py-[1px] text-[11px] font-medium text-dim">
                {c.label}
              </span>
            ))}
          </span>
        )}
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact icon={BuildingIcon} label="Company">
          {call.company ? (
            call.company.hubspotUrl ? (
              <a href={call.company.hubspotUrl} target="_blank" rel="noreferrer" className={LINK}>
                {call.company.name || call.company.domain || call.company.id} ↗
              </a>
            ) : (
              call.company.name || call.company.domain || call.company.id
            )
          ) : call.companyIds.length > 1 ? (
            <span className="text-dim">{call.companyIds.length} companies associated</span>
          ) : (
            <span className="text-faint">none associated</span>
          )}
        </Fact>
        <Fact icon={UserIcon} label="Contact">
          {call.contact ? (
            <>
              {call.contact.hubspotUrl ? (
                <a href={call.contact.hubspotUrl} target="_blank" rel="noreferrer" className={LINK}>
                  {call.contact.name || call.contact.email || call.contact.id} ↗
                </a>
              ) : (
                call.contact.name || call.contact.email || call.contact.id
              )}
              {call.contact.phone || call.contact.email ? (
                <div className="text-[12px] text-dim">{[call.contact.phone, call.contact.email].filter(Boolean).join(' · ')}</div>
              ) : null}
            </>
          ) : (
            <span className="text-faint" title="HubSpot names a callee only for calls placed from its own phone number.">not recorded</span>
          )}
        </Fact>
        <Fact icon={PhoneIcon} label="Numbers">
          {call.fromNumber || call.toNumber ? (
            <span className="tabular-nums">
              {formatPhone(call.fromNumber) || '—'} → {formatPhone(call.toNumber) || '—'}
            </span>
          ) : (
            <span className="text-faint" title="HubSpot recorded no phone numbers for this call.">not recorded</span>
          )}
        </Fact>
        <Fact icon={ClockIcon} label="Length">
          {duration != null ? (
            <span className="tabular-nums">
              {duration}
              {call.recordingDurationMs != null && formatDurationMs(call.recordingDurationMs) !== duration ? (
                <span className="text-dim"> · recording {formatDurationMs(call.recordingDurationMs)}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-faint" title="HubSpot holds no duration for this call — not recorded, not zero.">not recorded</span>
          )}
          {call.owner && <div className="text-[12px] text-dim">owner {call.owner.name || call.owner.email}</div>}
        </Fact>
      </div>

      <TranscriptSection call={call} />

      <section className={CARD}>
        <h3 className="m-0 mb-2 text-[14px] font-semibold text-ink">Summary</h3>
        {call.summary.length > 0 ? (
          <>
            <div className="mb-3 text-[12px] text-faint">HubSpot’s AI summary of the call, as HubSpot wrote it.</div>
            <SummarySections sections={call.summary} />
          </>
        ) : call.summaryText ? (
          <pre className="m-0 whitespace-pre-wrap font-sora text-[13.5px] leading-[1.55] text-ink">{call.summaryText}</pre>
        ) : phone ? (
          <div className="text-[13px] text-dim">
            HubSpot has not written a summary for this call{call.hasRecording ? ' yet. It usually follows within minutes of the call and reaches this page on the next sync' : ''}.
          </div>
        ) : call.hasRecording ? (
          <div className="text-[13px] text-dim">HubSpot holds no summary for this call.</div>
        ) : (
          <div className="text-[13px] text-dim">No summary: this call was logged by hand, so HubSpot kept no recording of it.</div>
        )}
      </section>

      <section className={CARD}>
        <h3 className="m-0 mb-2 flex items-center gap-x-1.5 text-[14px] font-semibold text-ink">
          <TextIcon className="h-[14px] w-[14px] text-faint" aria-hidden="true" />
          Notes
        </h3>
        {call.notes ? (
          <pre className="m-0 whitespace-pre-wrap font-sora text-[13.5px] leading-[1.55] text-ink">{call.notes}</pre>
        ) : (
          <div className="text-[13px] text-faint">The rep typed no notes on this call.</div>
        )}
      </section>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
        {call.reviewUrl && (
          <a href={call.reviewUrl} target="_blank" rel="noreferrer" className={LINK}>
            {call.hasRecording ? 'Play the recording in HubSpot ↗' : 'Open the call review in HubSpot ↗'}
          </a>
        )}
        {call.hubspotUrl && (
          <a href={call.hubspotUrl} target="_blank" rel="noreferrer" className={LINK}>
            Open the call record ↗
          </a>
        )}
        {call.syncedAt && (
          <span className="text-faint">
            mirrored {chicagoClock(call.syncedAt)} · {longDay(call.syncedAt.slice(0, 10))}
          </span>
        )}
      </div>
    </div>
  )
}
